import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MaterialLookupResponse } from '../../../type-definitions/lookups/responses/lookups';
import { SimpleLookupResult } from '../../../type-definitions/general/general';

@Injectable({
  providedIn: 'root',
})
export class DailyReportsService {
  http: HttpClient;

  constructor(http: HttpClient) {
    this.http = http;
  }

  getJobList(form: string, searchString: string, status: string) {
    const url = '/DailyReports/GetJobList?form=' + form + '&searchString=' + searchString + '&status=' + status;
    return this.http.get(url).toPromise();
  }

  getFileURL(pMDLID: number, jCFTKeyID: number, filenames: string, where: string, managePage?: boolean) {
    const url =
      '/DailyReports/GetFileURL?PMDLID=' +
      pMDLID +
      '&JCFTKeyID=' +
      jCFTKeyID +
      '&filenames=' +
      filenames +
      '&where=' +
      where +
      '&managePage=' +
      managePage;
    return this.http.get(url).toPromise();
  }

  saveFileZip(pmdlKeyIDList: number[], jCFTKeyIDList: number[], pageMode: string, outputFileName: string) {
    return this.http
      .post('/DailyReports/SaveFileZip', {
        pmdlKeyIDList: pmdlKeyIDList,
        jcftKeyIDList: jCFTKeyIDList,
        pageMode: pageMode,
        outputFileName: outputFileName,
      })
      .toPromise();
  }

  saveFilePdf(pmdlKeyIDList: number[], jCFTKeyIDList: number[], pageMode: string, outputFileName: string) {
    return this.http
      .post('/DailyReports/SaveFilePdf', {
        pmdlKeyIDList: pmdlKeyIDList,
        jcftKeyIDList: jCFTKeyIDList,
        pageMode: pageMode,
        outputFileName: outputFileName,
      })
      .toPromise();
  }

  downloadCompileZIPFile(zipFileName: string, generatedFileName: string) {
    const url = '/DailyReports/DownloadCompileZipFile?zipFileName=' + zipFileName + '&generatedFileName=' + generatedFileName;
    return this.http.get(url).toPromise();
  }

  downloadCompilePDFFile(pdfFilename: string, generatedFileName: string) {
    const url = '/DailyReports/DownloadCompilePdfFile?pdfFilename=' + pdfFilename + '&generatedFileName=' + generatedFileName;
    return this.http.get(url).toPromise();
  }

  setJob(lastUsedJCJMKeyID?: number, userKeyID?: number) {
    return this.http
      .post('/DailyReports/SetJob', {
        lastUsedJCJMKeyID: lastUsedJCJMKeyID,
        userKeyID: userKeyID,
      })
      .toPromise();
  }

  getProjectResultList(jCJMKeyID: string, startDatestr: string, endDatestr: string, showOnlyTickets?: boolean) {
    const url =
      '/DailyReports/GetProjectResultList?jCJMKeyID=' +
      jCJMKeyID +
      '&startDatestr=' +
      startDatestr +
      '&endDatestr=' +
      endDatestr +
      '&showOnlyTickets=' +
      showOnlyTickets;
    return this.http.get(url).toPromise();
  }

  getProjectHeader(pMDLKeyID: number, jCJMKeyID: number, datestr: string, phase: string) {
    const url =
      '/DailyReports/GetProjectHeader?pMDLKeyID=' + pMDLKeyID + '&jCJMKeyID=' + jCJMKeyID + '&datestr=' + datestr + '&phase=' + phase;
    return this.http.get(url).toPromise();
  }

  unsubmit(pMDLKeyID: number) {
    return this.http
      .post('/DailyReports/Unsubmit', {
        pMDLKeyID: pMDLKeyID,
      })
      .toPromise();
  }

  dailyReportDelete(pMDLKeyID: number) {
    return this.http
      .post('/DailyReports/DailyReportDelete', {
        pMDLKeyID: pMDLKeyID,
      })
      .toPromise();
  }

  getPO(pMDLKeyID: number) {
    const url = '/DailyReports/GetPO?pMDLKeyID=' + pMDLKeyID;
    return this.http.get(url).toPromise();
  }

  getUM() {
    const url = '/DailyReports/GetUM';
    return this.http.get<SimpleLookupResult[]>(url).toPromise();
  }

  getFieldsInformation(displayMode: string, pMDLKeyID: number) {
    const url = '/DailyReports/GetFieldsInformation?displayMode=' + displayMode + '&pMDLKeyID=' + pMDLKeyID;
    return this.http.get(url).toPromise();
  }

  getUserLookup(pMDLKeyID: number, pMDDKeyID: number, columnName: string, formFullName: string) {
    const url = '/DailyReports/GetUserLookup';
    const params = new HttpParams().set('pMDLKeyID', pMDLKeyID).set('columnName', columnName).set('pMDDKeyID', pMDDKeyID).set('form', formFullName);
    return this.http.get<SimpleLookupResult>(url, { params: params }).toPromise();
  }

  getWeather(
    year: number,
    month: number,
    day: number,
    zip: string = null,
    city: string = null,
    state: string = null,
    country: string,
    isforecast: boolean
  ) {
    const url =
      '/DailyReports/GetWeather' +
      '?year=' +
      year +
      '&month=' +
      month +
      '&day=' +
      day +
      '&zip=' +
      zip +
      '&city=' +
      city +
      '&state=' +
      state +
      '&country=' +
      country +
      '&isforecast=' +
      isforecast;
    return this.http.get(url).toPromise();
  }

  getAttachment(pMDLKeyID?: number, jCFTKeyID?: number, includeData = false) {
    const url = '/DailyReports/GetAttachment?pMDLKeyID=' + pMDLKeyID + '&jCFTKeyID=' + jCFTKeyID + '&includeData=' + includeData;
    return this.http.get(url).toPromise();
  }

  deleteAttachment(attachmentID: number, uniqueAttchID: string, database: boolean) {
    return this.http
      .post('/DailyReports/DeleteAttachment', {
        attachmentID: attachmentID,
        uniqueAttchID: uniqueAttchID,
        database: database,
      })
      .toPromise();
  }

  addVPAttachment(requestObj: any) {
    return this.http
      .post('/Document/AddVPAttachment', {
        fileName: requestObj.fileName,
        fileDescription: requestObj.fileDescription,
        fileData: requestObj.fileData,
        moduleID: requestObj.moduleID,
        recordKeyID: requestObj.recordKeyID,
        fileAttachmentID: requestObj.fileAttachmentID,
        fileAttachmentType: requestObj.fileAttachmentType,
        recordGuid: requestObj.recordGuid,
        attachmentType: requestObj.attachmentType,
        attachmentDescription: requestObj.attachmentDescription,
      })
      .toPromise();
  }

  setProjectDetails(requestObj: any) {
    return this.http
      .post('/DailyReports/SetProjectDetails', {
        pMDDKeyID: requestObj.pMDDKeyID,
        pMDLKeyID: requestObj.pMDLKeyID,
        logType: requestObj.logType,
        pMFirmeKeyID: requestObj.pMFirmeKeyID,
        pMContactKeyID: requestObj.pMContactKeyID,
        eMEMKeyID: requestObj.eMEMKeyID,
        pMPFKeyID: requestObj.pMPFKeyID,
        hQMTKeyID: requestObj.hQMTKeyID,
        worked: requestObj.worked,
        workDescription: requestObj.workDescription,
        arriveTimestr: requestObj.arriveTimestr,
        departTimestr: requestObj.departTimestr,
        numberOfEmployees: requestObj.numberOfEmployees,
        location: requestObj.location,
        visitor: requestObj.visitor,
        quantity: requestObj.quantity,
        delTicket: requestObj.delTicket,
        uM: requestObj.uM,
        supervisor: requestObj.supervisor,
        pO: requestObj.pO,
        userFieldName: requestObj.userFieldName,
        userFieldValue: requestObj.userFieldValue,
        apprentices: requestObj.apprentices,
        journeyman: requestObj.journeyman,
        phase: requestObj.phase,
        eMFreeText: requestObj.eMFreeText,
        mTFreeText: requestObj.mTFreeText,
        udDefaultName: requestObj.udDefaultName,
        udDefaultValue: requestObj.udDefaultValue,
      })
      .toPromise();
  }

  getFieldTicketDetails(jCFTKeyID: number) {
    const url = '/DailyReports/GetFieldTicketDetails?jCFTKeyID=' + jCFTKeyID;
    return this.http.get(url).toPromise();
  }

  getFieldTicketSetting(jCFTKeyID: number) {
    const url = '/DailyReports/GetFieldTicketSetting?jCFTKeyID=' + jCFTKeyID;
    return this.http.get(url).toPromise();
  }

  getContactNameEmployee(jCJMKeyID: number, logType: number, pMFirmKeyID?: number) {
    const url = '/DailyReports/GetContactNameEmployee?jCJMKeyID=' + jCJMKeyID + '&logType=' + logType + '&pMFirmKeyID=' + pMFirmKeyID;
    return this.http.get(url).toPromise();
  }

  getCTTemplateByJCFTKeyID(jCFTId) {
    const url = '/DailyReports/GetCTTemplateByJCFTKeyID?jCFTKeyID=' + jCFTId;
    return this.http.get(url).toPromise();
  }

  getCustomerPO(jCJMKeyID) {
    const url = '/DailyReports/GetCustomerPO?jCJMKeyID=' + jCJMKeyID;
    return this.http.get(url).toPromise();
  }

  getJCFieldTicket(jCFTId) {
    const url = '/DailyReports/GetJCFieldTicket?jCFTKeyID=' + jCFTId;
    return this.http.get(url).toPromise();
  }

  getPhasesByJCJMKeyID(jCJMKeyID: number, laborOnly: boolean) {
    const url = '/DailyReports/GetPhasesByJCJMKeyID?jCJMKeyID=' + jCJMKeyID + '&laborOnly=' + laborOnly;
    return this.http.get(url).toPromise();
  }

  deleteProjectDetail(pMDDKeyID: number) {
    return this.http
      .post('/DailyReports/DeleteProjectDetail', {
        pMDDKeyID: pMDDKeyID,
      })
      .toPromise();
  }

  editLocation(jCJMKeyID: number, location: string, locationDescription: string) {
    return this.http
      .post('/DailyReports/EditLocation', {
        jCJMKeyID: jCJMKeyID,
        location: location,
        locationDescription: locationDescription,
      })
      .toPromise();
  }

  getLocation(jCJMKeyID: number) {
    const url = '/DailyReports/GetLocation?jCJMKeyID=' + jCJMKeyID;
    return this.http.get(url).toPromise();
  }

  setLocation(jCJMKeyID: number, location: string, locationDescription: string) {
    return this.http
      .post('/DailyReports/SetLocation', {
        jCJMKeyID: jCJMKeyID,
        location: location,
        locationDescription: locationDescription,
      })
      .toPromise();
  }

  getFirmName(jCJMKeyID: number, searchText: string) {
    const url = '/DailyReports/GetFirmName?jCJMKeyID=' + jCJMKeyID + '&searchText=' + searchText;
    return this.http.get(url).toPromise();
  }

  getEmployeeList(jCJMKeyID: number, searchText: string) {
    const url = '/DailyReports/GetEmployeeList?jCJMKeyID=' + jCJMKeyID + '&searchText=' + searchText;
    return this.http.get(url).toPromise();
  }

  updateGlobeEmployeeList(pREHKeyID: number, jCJMKeyID: number) {
    return this.http
      .post('/DailyReports/UpdateGlobeEmployeeList', {
        pREHKeyID: pREHKeyID,
        jCJMKeyID: jCJMKeyID,
      })
      .toPromise();
  }

  cloneProject(cloneToPMDLKeyID: number, cloneFromPMDLKeyID: number) {
    return this.http
      .post('/DailyReports/CloneProject', {
        cloneToPMDLKeyID: cloneToPMDLKeyID,
        cloneFromPMDLKeyID: cloneFromPMDLKeyID,
      })
      .toPromise();
  }

  getHGCloneFromTickets(jCJMKeyIDString: string, cloneFromDateString: string) {
    const url = '/DailyReports/GetHGCloneFromTickets?jCJMKeyIDString=' + jCJMKeyIDString + '&cloneFromDateString=' + cloneFromDateString;
    return this.http.get(url).toPromise();
  }

  getDailyReportsCustomText() {
    const url = '/DailyReports/GetDailyReportsCustomText';
    return this.http.get(url).toPromise();
  }

  getRequireDigitalSignature() {
    const url = '/DailyReports/GetRequireDigitalSignature';
    return this.http.get(url).toPromise();
  }

  getDistribution(jCJMKeyID: number, pMIMKeyID?: number) {
    const url = '/DailyReports/GetDistribution?jCJMKeyID=' + jCJMKeyID + '&pMIMKeyID=' + pMIMKeyID;
    return this.http.get(url).toPromise();
  }

  getCompanies() {
    const url = '/Account/GetCompanies';
    return this.http.get(url).toPromise();
  }

  submitProject(pMDLKeyID: number, signatureData: any, signatureLat?: number, signatureLong?: number) {
    return this.http
      .post('/DailyReports/SubmitProject', {
        pMDLKeyID: pMDLKeyID,
        signatureData: signatureData,
        signatureLat: signatureLat,
        signatureLong: signatureLong,
      })
      .toPromise();
  }

  //SendDailyReports
  sendDailyReports(
    pMDLID: any,
    jCFTKeyID: number,
    filenames: string,
    toEmail: string[],
    subject: string,
    message: string,
    newFilenames: string[],
    newFiles: any,
    where: string,
    queueType: string,
    notificationMode: string,
    sMSTo: string
  ) {
    return this.http
      .post('/DailyReports/SendDailyReports', {
        PMDLID: pMDLID,
        JCFTKeyID: jCFTKeyID,
        filenames: filenames,
        toEmail: toEmail,
        subject: subject,
        message: message,
        newFilenames: newFilenames,
        newFiles: newFiles,
        where: where,
        queueType: queueType,
        notificationMode: notificationMode,
        sMSTo: sMSTo,
      })
      .toPromise();
  }

  getProjectDetails(pMDLKeyID: number) {
    const url = '/DailyReports/GetProjectDetails?pMDLKeyID=' + pMDLKeyID;
    return this.http.get(url).toPromise();
  }

  getVPAttachmentData(attachmentID: number, maxWidth?: number, turnOnMaxWidth?: boolean) {
    const url =
      '/DailyReports/GetVPAttachmentData?attachmentID=' + attachmentID + '&maxWidth=' + maxWidth + '&turnOnMaxWidth=' + turnOnMaxWidth;
    return this.http.get(url).toPromise();
  }

  getVCMttachmentData(contentTypeName: string, docID: number) {
    const url = '/DailyReports/GetVCMttachmentData?contentTypeName=' + contentTypeName + '&docID=' + docID;
    return this.http.get(url).toPromise();
  }

  setProjectHeader(requestObj: any) {
    return this.http
      .post('/DailyReports/SetProjectHeader', {
        workDescription: requestObj.workDescription,
        weather: requestObj.weather,
        wind: requestObj.wind,
        tempHigh: requestObj.tempHigh,
        tempLow: requestObj.tempLow,
        udRainfall: requestObj.udRainfall,
        pMDLKeyID: requestObj.pMDLKeyID,
        workDescriptionShort: requestObj.workDescriptionShort,
        userFieldName: requestObj.userFieldName,
        userFieldValue: requestObj.userFieldValue,
      })
      .toPromise();
  }

  getContactName(jCJMKeyID: number, pMFirmKeyID: number) {
    const url = '/DailyReports/GetContactName?jCJMKeyID=' + jCJMKeyID + '&pMFirmKeyID=' + pMFirmKeyID;
    return this.http.get(url).toPromise();
  }

  getMaterial(pMDLKeyID: number, searchString: string, locationKeyID?: number) {
    const url = '/DailyReports/GetMaterial';
    const params = new HttpParams().set('pMDLKeyID', pMDLKeyID).set('searchString', searchString).set('locationKeyID', locationKeyID);

    return this.http.get<MaterialLookupResponse[]>(url, { params: params }).toPromise();
  }

  getEquipment(pMDLKeyID: number, searchString: string) {
    const url = '/DailyReports/GetEquipment?pMDLKeyID=' + pMDLKeyID + '&searchString=' + searchString;
    return this.http.get(url).toPromise();
  }

  getTabs(pMDLKeyID) {
    const url = '/DailyReports/GetTabs?pMDLKeyID=' + pMDLKeyID;
    return this.http.get(url).toPromise();
  }

  getSignaturesByJCFTKeyID(jCFTKeyID) {
    const url = '/DailyReports/GetSignaturesByJCFTKeyID?jCFTKeyID=' + jCFTKeyID;
    return this.http.get(url).toPromise();
  }

  getEmployeesByPRCoPRGroup(pRCo, pRGroup) {
    const url = '/DailyReports/GetEmployeesByPRCoPRGroup?pRCo=' + pRCo + '&pRGroup=' + pRGroup;
    return this.http.get(url).toPromise();
  }

  getBulkAddFieldsByJCFTKeyID(jCFTId, crewTemplateID) {
    const url = '/DailyReports/GetBulkAddFieldsByJCFTKeyID?jCFTKeyID=' + jCFTId + '&crewTemplateID=' + crewTemplateID;
    return this.http.get(url).toPromise();
  }

  submitSavedTimecard(requestObj) {
    const url = '/DailyReports/SubmitSavedTimeCard';
    return this.http.post(url, requestObj).toPromise();
  }

  getMaterialItemByTicket(jCFTKeyID) {
    const url = '/DailyReports/GetMaterialItemByTicket?jCFTKeyID=' + jCFTKeyID;
    return this.http.get(url).toPromise();
  }

  getMaterialCost(locationKeyID, jCCo, job, material, uM) {
    const url =
      '/DailyReports/GetMaterialCost?iNLMKeyID=' + locationKeyID + '&jCCo=' + jCCo + '&job=' + job + '&material=' + material + '&uM=' + uM;
    return this.http.get(url).toPromise();
  }

  getMaterialPhasesByJob(jCJMKeyID, section) {
    const url = '/DailyReports/GetProgressPhasesByJob?jCJMKeyID=' + jCJMKeyID + '&section=' + section;
    return this.http.get(url).toPromise();
  }

  addEditMaterialItem(
    jCFTKeyID,
    materialItemID,
    material,
    phase,
    costType,
    materialDescription,
    uM,
    units,
    unitPrice,
    jCType,
    iNCo,
    iNLocation,
    isMaterialInList
  ) {
    return this.http
      .post('/DailyReports/AddEditMaterialItem', {
        jCFTKeyID: jCFTKeyID,
        materialItemID: materialItemID,
        material: material,
        phase: phase,
        costType: costType,
        materialDescription: materialDescription,
        uM: uM,
        units: units,
        unitPrice: unitPrice,
        jCType: jCType,
        iNCo: iNCo,
        iNLocation: iNLocation,
        isMaterialInList: isMaterialInList,
      })
      .toPromise();
  }

  removeMaterialItem(jCFTKeyID, materialItemID) {
    return this.http.post('/DailyReports/RemoveMaterialItem', { jCFTKeyID: jCFTKeyID, materialItemID: materialItemID }).toPromise();
  }

  getINLocations(section: string) {
    let requestString = '/Equipment/GetINLocations';

    if (section != null) requestString += '?section=' + section;

    return this.http.get(requestString).toPromise();
  }

  getProgressPhasesByJob(jCJMKeyID) {
    const url = '/DailyReports/GetProgressPhasesByJob?jCJMKeyID=' + jCJMKeyID;
    return this.http.get(url).toPromise();
  }

  getProgressItemsByJCFTKeyID(jCFTKeyID) {
    const url = '/DailyReports/GetProgressItemsByJCFTKeyID?jCFTKeyID=' + jCFTKeyID;
    return this.http.get(url).toPromise();
  }

  addProgressItem(jCFTKeyID, jCJPKeyID, costType) {
    const url = '/DailyReports/AddProgressItem';
    return this.http
      .post(url, {
        jCFTKeyID: jCFTKeyID,
        jCJPKeyID: jCJPKeyID,
        costType: costType,
      })
      .toPromise();
  }

  updateProgressItem(progressItemID, actualUnits, notes) {
    const url = '/DailyReports/UpdateProgressItem';
    return this.http
      .post(url, {
        progressItemID: progressItemID,
        actualUnits: actualUnits,
        notes: notes,
      })
      .toPromise();
  }

  removeProgressItem(jCFTKeyID, progressItemID) {
    const url = '/DailyReports/RemoveProgressItem';
    return this.http.post(url, { jCFTKeyID: jCFTKeyID, progressItemID: progressItemID }).toPromise();
  }

  deleteTicket(jCFTKeyID: number) {
    const url = '/DailyReports/DeleteTicket';
    return this.http
      .post(url, {
        jCFTKeyID: jCFTKeyID,
      })
      .toPromise();
  }

  processMaterialByJCFTKeyID(jCFTKeyID?: number) {
    const url = '/DailyReports/ProcessMaterialByJCFTKeyID';
    return this.http
      .post(url, {
        jCFTKeyID: jCFTKeyID,
      })
      .toPromise();
  }

  processProgressByJCFTKeyID(jCFTKeyID?: number) {
    const url = '/DailyReports/ProcessProgressByJCFTKeyID';
    return this.http
      .post(url, {
        jCFTKeyID: jCFTKeyID,
      })
      .toPromise();
  }

  unsubmitTicket(jCFTKeyID: number) {
    const url = '/DailyReports/UnsubmitTicket';
    return this.http
      .post(url, {
        jCFTKeyID: jCFTKeyID,
      })
      .toPromise();
  }

  submitTicket(jCFTKeyID, signatureImage, signaturePrintName, signatureLatitude, signatureLongitude, signature2Image, signature2PrintName) {
    const requestObj: any = {
      jCFTKeyID: jCFTKeyID,
      signatureImage: signatureImage,
      signaturePrintName: signaturePrintName,
      signatureLatitude: signatureLatitude,
      signatureLongitude: signatureLongitude,
    };
    if (signature2Image != null) {
      requestObj.signature2Image = signature2Image;
      requestObj.signature2PrintName = signature2PrintName;
    }
    const url = '/DailyReports/SubmitTicket';
    return this.http.post(url, requestObj).toPromise();
  }

  approveTicket(jCFTKeyID: number, filename: string) {
    const url = '/DailyReports/ApproveTicket';
    return this.http
      .post(url, {
        jCFTKeyID: jCFTKeyID,
        filename: filename,
      })
      .toPromise();
  }

  setJCFieldTicket(
    jCFTKeyID,
    ticketDescription,
    orderedBy,
    ownerJobNo,
    customerPO,
    address,
    city,
    state,
    zip,
    country,
    contact,
    phone,
    notes,
    userFieldName,
    userFieldValue,
    pMDLKeyID
  ) {
    const url = '/DailyReports/SetJCFieldTicket';
    return this.http
      .post(url, {
        jCFTKeyID: jCFTKeyID,
        ticketDescription: ticketDescription,
        orderedBy: orderedBy,
        ownerJobNo: ownerJobNo,
        customerPO: customerPO,
        address: address,
        city: city,
        state: state,
        zip: zip,
        country: country,
        contact: contact,
        phone: phone,
        notes: notes,
        userFieldName: userFieldName,
        userFieldValue: userFieldValue,
        pMDLKeyID: pMDLKeyID,
      })
      .toPromise();
  }
}
